<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Icon } from '@iconify/vue';
import { usePreSetStore } from '../stores/preSetStore';
import { isSg, isMy } from '@/features/startTask/preset_task/jurisdictionResolver';
import PrepareArEntityPanel from './PrepareArEntityPanel.vue';
import {
  getEFilingWalkthroughUrl,
  getStartTaskWalkthroughUrl,
} from '@/shared/utils/tutorialUrls';
import type { PreSetTask, PreSetDocument, PreSetSelection, PreSetDocCode } from '../types/preset.types';

type PresetTutorialType = 'startTask' | 'efiling';
const showTutorial = ref(false);
const activeTutorialType = ref<PresetTutorialType>('startTask');
const startTaskTutorialSrc = computed(() => getStartTaskWalkthroughUrl());
const eFilingTutorialSrc = computed(() => getEFilingWalkthroughUrl());
const activeTutorialSrc = computed(() =>
  activeTutorialType.value === 'efiling' ? eFilingTutorialSrc.value : startTaskTutorialSrc.value,
);
const activeTutorialTitle = computed(() =>
  activeTutorialType.value === 'efiling' ? 'E-Filing Tutorial' : 'Start Task Tutorial',
);
const jurisdictionLabel = computed(() => (isSg() ? 'SG' : 'MY'));

function openPresetTutorial(type: PresetTutorialType) {
  activeTutorialType.value = type;
  showTutorial.value = true;
}

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
  (e: 'select', selection: PreSetSelection): void;
  (e: 'skip'): void;
}>();

const preSetStore = usePreSetStore();

// ─── State ────────────────────────────────────────────────────
const searchQuery = ref('');
const selectedTask = ref<PreSetTask | null>(null);
const taskDocuments = ref<PreSetDocument[]>([]);
const loadingDocs = ref(false);

// ─── Start-task 타입 전환 (mockup 시안 B: Preset · T-Doc AI · Prepare AR) ──
type StartTaskType = 'preset' | 'mypreset' | 'tdoc' | 'ar';
const activeType = ref<StartTaskType>('preset');
const startTaskTypes = computed(() => {
  const types: { key: StartTaskType; label: string; sub: string; icon: string }[] = [
    { key: 'preset', label: 'Preset', sub: 'Lettaga templates', icon: 'mdi:format-list-checks' },
    { key: 'mypreset', label: 'My Preset', sub: 'Your own templates', icon: 'mdi:star-outline' },
    { key: 'tdoc', label: 'T-Doc AI', sub: 'Upload & AI', icon: 'mdi:creation' },
  ];
  if (isMy()) {
    types.push({ key: 'ar', label: 'Prepare AR', sub: 'Annual Return', icon: 'mdi:calendar-check' });
  }
  return types;
});
// T-Doc AI 안내 패널 단계 (mockup STEPS)
const tdocSteps = ['Category', 'Template', 'Extract', 'Variables', 'Entities', 'Generate', 'Preview', 'Editor'];

// ─── Computed ─────────────────────────────────────────────────

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

/** Preset · My Preset 는 동일한 리스트+프리뷰 UI 를 공유한다. */
const isPresetView = computed(() => activeType.value === 'preset' || activeType.value === 'mypreset');
const isMyPreset = computed(() => activeType.value === 'mypreset');
const listLoading = computed(() => (isMyPreset.value ? preSetStore.myLoading : preSetStore.loading));

/** DOC_CODES JSON 파싱 */
function parseDocCodes(task: PreSetTask): PreSetDocCode[] {
  if (!task.DOC_CODES) return [];
  try {
    const parsed = JSON.parse(task.DOC_CODES);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

/**
 * 업무 가능 단계(Document / T-Sign / E-Filing / Register) — 프리셋별 task config 플래그(DB) 기준.
 *   가능한 단계만 아이콘으로 표시하고, 못 하는 단계는 아예 표시하지 않는다.
 */
const TASK_CAPS = [
  { field: 'IS_DOC', label: 'Doc', icon: 'mdi:file-document-edit-outline', color: '#1B84FF', bg: '#eaf3ff' },
  { field: 'IS_T_SIGN', label: 'T-Sign', icon: 'mdi:draw-pen', color: '#7a4bd1', bg: '#f2ecfd' },
  { field: 'IS_E_FILING', label: 'E-Filing', icon: 'mdi:cloud-upload-outline', color: '#0f9d77', bg: '#e6f6f0' },
  { field: 'IS_REG_REGISTER', label: 'Register', icon: 'mdi:book-account-outline', color: '#c2571e', bg: '#fdf0e8' },
] as const;
function isCapOn(v: unknown): boolean {
  return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';
}
function capsOf(task: PreSetTask) {
  return TASK_CAPS.filter((c) => isCapOn(task[c.field]));
}

/** 카테고리별 그룹화된 태스크 목록 */
const groupedTasks = computed(() => {
  const groups: Array<{
    categoryId: number;
    categoryName: string;
    tasks: Array<PreSetTask & { docCount: number; menuName: string }>;
    _order: number;
  }> = [];

  let order = 0;
  // ⚠️ 헤더 라벨은 "카테고리"(level 1) 기준이므로, 한 카테고리에 메뉴(level 2)가 여러 개면
  //   메뉴별로 그룹을 만들지 말고 "카테고리 하나"로 병합해야 헤더가 중복되지 않는다.
  //   (예: "Update shares" 카테고리 = Allotment / Transfer 두 메뉴 → 하나의 UPDATE SHARES 그룹)
  //   Preset / My Preset 여부에 따라 소스 트리·태스크를 전환한다.
  const sourceTree = isMyPreset.value ? preSetStore.myCategoryTree : preSetStore.categoryTree;
  const getTasks = (menuId: number) =>
    isMyPreset.value ? preSetStore.getMyTasksByMenu(menuId) : preSetStore.getTasksByMenu(menuId);
  const groupByCat = new Map<number, (typeof groups)[number]>();
  for (const cat of sourceTree) {
    for (const menu of cat.children || []) {
      const tasks = getTasks(menu.CATEGORY_ID);
      if (tasks.length === 0) continue;

      let filtered = tasks;
      if (searchQuery.value.trim()) {
        const q = searchQuery.value.toLowerCase();
        filtered = tasks.filter(t => t.TASK_NAME.toLowerCase().includes(q));
      }
      if (filtered.length === 0) continue;

      let group = groupByCat.get(cat.CATEGORY_ID);
      if (!group) {
        group = {
          categoryId: cat.CATEGORY_ID,
          categoryName: cat.CATEGORY_NAME,
          tasks: [],
          _order: order++,
        };
        groupByCat.set(cat.CATEGORY_ID, group);
        groups.push(group);
      }
      group.tasks.push(...filtered.map(t => ({
        ...t,
        docCount: parseDocCodes(t).length,
        menuName: menu.CATEGORY_NAME,
      })));
    }
  }

  // 각 카테고리 그룹 내부 정렬 — 즐겨찾기 우선, 그다음 SORT_ORDER (JS sort 안정성으로 메뉴 순서 유지).
  for (const group of groups) {
    group.tasks.sort((a, b) => {
      const aFav = preSetStore.favoriteIds.has(a.PRESET_TASK_ID) ? 0 : 1;
      const bFav = preSetStore.favoriteIds.has(b.PRESET_TASK_ID) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;
      return a.SORT_ORDER - b.SORT_ORDER;
    });
  }

  // INCORPORATION 카테고리를 상단으로 고정 (요청사항)
  const priority = (name: string) => {
    const n = String(name || '').toUpperCase();
    if (n.includes('INCORPORATION')) return 0;
    return 1;
  };
  return groups.sort((a, b) => {
    const pa = priority(a.categoryName);
    const pb = priority(b.categoryName);
    if (pa !== pb) return pa - pb;
    return a._order - b._order;
  });
});

// ─── Methods ──────────────────────────────────────────────────

async function selectTask(task: PreSetTask) {
  selectedTask.value = task;
  taskDocuments.value = [];
  orderSaved.value = false;
  dragIndex.value = null;
  overIndex.value = null;
  if (parseDocCodes(task).length > 0) {
    loadingDocs.value = true;
    try {
      taskDocuments.value = isMyPreset.value
        ? await preSetStore.fetchMyTaskDocuments(task.PRESET_TASK_ID)
        : await preSetStore.fetchTaskDocuments(task.PRESET_TASK_ID);
    } finally {
      loadingDocs.value = false;
    }
  }
}

// ─── 문서 순서 드래그앤드롭 (클라이언트별 저장) ───────────────────
const dragIndex = ref<number | null>(null);
const overIndex = ref<number | null>(null);
const savingOrder = ref(false);
const orderSaved = ref(false);

function onDocDragStart(idx: number, e: DragEvent) {
  dragIndex.value = idx;
  orderSaved.value = false;
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  }
}
function onDocDragOver(idx: number, e: DragEvent) {
  e.preventDefault();
  overIndex.value = idx;
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
}
function onDocDrop(idx: number, e: DragEvent) {
  e.preventDefault();
  const from = dragIndex.value;
  dragIndex.value = null;
  overIndex.value = null;
  if (from === null || from === idx) return;
  const arr = [...taskDocuments.value];
  const [moved] = arr.splice(from, 1);
  arr.splice(idx, 0, moved);
  taskDocuments.value = arr;
  void persistOrder();
}
function onDocDragEnd() {
  dragIndex.value = null;
  overIndex.value = null;
}

/** 현재 순서를 클라이언트별로 저장 (docCode 배열) */
async function persistOrder() {
  if (!selectedTask.value) return;
  const order = taskDocuments.value.map(d => d.docCode);
  savingOrder.value = true;
  orderSaved.value = false;
  try {
    if (isMyPreset.value) {
      await preSetStore.saveMyTaskDocOrder(selectedTask.value.PRESET_TASK_ID, order);
    } else {
      await preSetStore.saveTaskDocOrder(selectedTask.value.PRESET_TASK_ID, order);
    }
    orderSaved.value = true;
  } catch (err) {
    console.error('[PreSetModal] save doc order failed:', err);
  } finally {
    savingOrder.value = false;
  }
}

function confirmSelect() {
  if (!selectedTask.value) return;
  const cats = isMyPreset.value ? preSetStore.myCategories : preSetStore.categories;
  const menu = cats.find(c => c.CATEGORY_ID === selectedTask.value!.CATEGORY_ID);
  const parentCategory = menu?.PARENT_ID
    ? cats.find(c => c.CATEGORY_ID === menu.PARENT_ID) || null
    : null;

  emit('select', {
    task: selectedTask.value,
    category: menu || { CATEGORY_ID: 0, PARENT_ID: null, CATEGORY_NAME: '', CATEGORY_LEVEL: 2, COUNTRY_CD: 'MY', SORT_ORDER: 0 },
    parentCategory,
    documents: taskDocuments.value,
    isMyPreset: isMyPreset.value,
  });
  isOpen.value = false;
}

function handleCancel() {
  emit('skip');
  isOpen.value = false;
}

// SG 전용 — PreSet 사용 안 하고 기존 카드 메뉴(`/tasks/new`) 로 이동.
//   MY 는 PreSet 만 지원하므로 버튼 미노출.
const router = useRouter();
const { t } = useI18n();

// My Preset(구독형) 신청 페이지로 이동. 구독 전에는 안내 패널의 CTA에서 호출. (구독 후 기능은 추후 구현)
function goToMyPreset() {
  router.push('/marketplace/my-preset');
}
const showClassicMenuLink = computed(() => isSg());
function handleUseClassicMenu() {
  isOpen.value = false;
  router.push({ path: '/tasks/new', query: {} });
}

// T-Doc AI 진입 — T-Doc(Doc Manager) 메뉴의 버튼과 동일 경로(/tasks/new?service=tdocai)
function openTDocAi() {
  isOpen.value = false;
  router.push({ path: '/tasks/new', query: { service: 'tdocai' } });
}
function onPrepareAr(entityCd: string) {
  isOpen.value = false;
  router.push(`/annual-return_my/${entityCd}`);
}

// ─── Init ─────────────────────────────────────────────────────

watch(() => props.modelValue, async (val) => {
  if (!val) return;
  // 매번 열 때 이전 선택 / 검색어 초기화 — 이전에 고른 task 가 그대로 남아있던 버그.
  selectedTask.value = null;
  taskDocuments.value = [];
  searchQuery.value = '';
  loadingDocs.value = false;
  activeType.value = 'preset';
  try {
    // 항상 재조회 (이전 에러로 빈 배열일 수 있음). Preset + My Preset 동시 로드.
    await Promise.all([
      preSetStore.loadAll(),
      preSetStore.loadMyAll().catch((err) => console.error('[PreSetModal] loadMyAll failed:', err)),
    ]);
  } catch (err) {
    console.error('[PreSetModal] loadAll failed:', err);
  }
});

// 탭 전환 시 이전 선택/검색 초기화 (Preset ↔ My Preset 교차 오염 방지)
watch(activeType, () => {
  selectedTask.value = null;
  taskDocuments.value = [];
  searchQuery.value = '';
  loadingDocs.value = false;
  orderSaved.value = false;
});
</script>

<template>
  <v-dialog v-model="isOpen" max-width="1180" persistent>
    <v-card class="preset-modal-card">
      <!-- Header -->
      <div class="px-5 pt-4 pb-0 d-flex align-start justify-space-between">
        <div>
          <div style="font-size: 18px; font-weight: 700; color: #1a1a2e;">Start task</div>
          <div style="font-size: 12px; color: #888; margin-top: 2px;">Pick a Preset, T-Doc AI, or prepare an Annual Return.</div>
        </div>
        <div class="d-flex align-center ga-2">
          <v-menu location="bottom end">
            <template #activator="{ props: menuProps }">
              <v-btn v-bind="menuProps" variant="tonal" color="primary" size="small">
                <v-icon start size="16">mdi-play-circle-outline</v-icon>
                Tutorial
                <v-icon end size="16">mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list density="compact" min-width="220">
              <v-list-item
                prepend-icon="mdi-rocket-launch-outline"
                :title="`Start Task Tutorial (${jurisdictionLabel})`"
                subtitle="Create and manage tasks"
                @click="openPresetTutorial('startTask')"
              />
              <v-list-item
                prepend-icon="mdi-file-send-outline"
                :title="`E-filing Tutorial (${jurisdictionLabel})`"
                subtitle="Submit to registry"
                @click="openPresetTutorial('efiling')"
              />
            </v-list>
          </v-menu>
          <v-btn icon variant="text" size="small" @click="handleCancel">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Body -->
      <div class="preset-modal-body">
        <!-- 타입 전환 컬럼 (Preset · T-Doc AI · Prepare AR) -->
        <div class="preset-type-col">
          <button
            v-for="ty in startTaskTypes"
            :key="ty.key"
            type="button"
            class="preset-type-btn"
            :class="{ on: activeType === ty.key }"
            @click="activeType = ty.key"
          >
            <Icon :icon="ty.icon" width="20" />
            <span class="ptb-text">
              <b>{{ ty.label }}</b>
              <small>{{ ty.sub }}</small>
            </span>
          </button>
        </div>

        <!-- ===== Preset / My Preset 뷰 (검색 + 리스트 + 프리뷰) ===== -->
        <template v-if="isPresetView">
        <!-- 왼쪽: 탭 + 검색 + 리스트 -->
        <div style="width: 440px; border-right: 1px solid #eee; display: flex; flex-direction: column; overflow: hidden;">
          <!-- 검색 -->
          <div class="px-4 pb-2">
            <v-text-field
              v-model="searchQuery"
              variant="outlined"
              density="compact"
              placeholder="Search presets..."
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              style="font-size: 13px;"
            />
          </div>

          <!-- 태스크 리스트 -->
          <div class="flex-grow-1 overflow-y-auto px-4 pb-3">
            <div v-if="listLoading" class="text-center py-6">
              <v-progress-circular indeterminate size="24" />
            </div>
            <div v-else-if="groupedTasks.length === 0" class="text-center py-6" style="color: #999; font-size: 13px;">
              <template v-if="isMyPreset">
                <Icon icon="mdi:star-outline" width="32" color="#d0d0d0" class="mb-2" />
                <div>No My Presets yet.</div>
                <v-btn class="mt-3" color="primary" variant="tonal" size="small" @click="goToMyPreset">
                  <Icon icon="mdi:star-outline" width="16" class="mr-1" />
                  {{ t('marketplace.mypreset.page.requestNew') }}
                </v-btn>
              </template>
              <template v-else>No presets found</template>
            </div>
            <template v-else>
              <div v-for="group in groupedTasks" :key="group.categoryId" class="mb-1">
                <!-- 카테고리 헤더 -->
                <div class="category-label">
                  <span class="category-dot" />
                  <span>{{ group.categoryName.toUpperCase() }}</span>
                  <span
                    v-if="selectedTask && group.tasks.some(t => t.PRESET_TASK_ID === selectedTask!.PRESET_TASK_ID)"
                    class="selected-badge"
                  >
                    &mdash; SELECTED
                  </span>
                </div>

                <!-- 태스크 아이템 -->
                <div
                  v-for="task in group.tasks"
                  :key="task.PRESET_TASK_ID"
                  class="task-item"
                  :class="{ 'task-item-selected': selectedTask?.PRESET_TASK_ID === task.PRESET_TASK_ID }"
                  @click="selectTask(task)"
                >
                  <div class="task-icon">
                    <Icon icon="mdi:file-document-outline" width="20" color="#8b8fa3" />
                  </div>
                  <div class="task-info">
                    <!-- 이름이 길어 잘릴 때 hover tooltip 으로 전체 텍스트 노출 (Vuetify v-tooltip — 빠른 표시 + 일관 스타일) -->
                    <v-tooltip location="top" :open-delay="200" max-width="380">
                      <template #activator="{ props: tipProps }">
                        <div
                          v-bind="tipProps"
                          class="task-name"
                          :style="{ color: 'rgb(var(--v-theme-on-surface))' }"
                        >
                          {{ task.TASK_NAME }}
                        </div>
                      </template>
                      <span>{{ task.TASK_NAME }}</span>
                    </v-tooltip>
                    <v-tooltip location="bottom" :open-delay="200" max-width="380">
                      <template #activator="{ props: tipProps }">
                        <div
                          v-bind="tipProps"
                          class="task-meta"
                          :style="{ color: 'rgba(var(--v-theme-on-surface), 0.7)' }"
                        >
                          {{ task.docCount }} documents · {{ task.menuName }}
                        </div>
                      </template>
                      <span>{{ task.docCount }} documents · {{ task.menuName }}</span>
                    </v-tooltip>
                    <!-- 업무 가능 단계 — 가능한 것만 표시 -->
                    <div v-if="capsOf(task).length" class="task-caps">
                      <span
                        v-for="c in capsOf(task)"
                        :key="c.field"
                        class="task-cap"
                        :style="{ color: c.color, background: c.bg }"
                        :title="`${c.label} available`"
                      >
                        <Icon :icon="c.icon" width="12" />{{ c.label }}
                      </span>
                    </div>
                  </div>
                  <div v-if="task.docCount > 0" class="doc-count-badge">
                    {{ task.docCount }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- 오른쪽: 프리뷰 -->
        <div class="flex-grow-1 d-flex flex-column" style="overflow: hidden;">
          <!-- 미선택 상태 -->
          <div v-if="!selectedTask" class="flex-grow-1 d-flex flex-column align-center justify-center" style="color: #aaa;">
            <Icon icon="mdi:file-document-multiple-outline" width="48" color="#d0d0d0" class="mb-3" />
            <div style="font-size: 15px; font-weight: 600; color: #666;">Select a preSet</div>
            <div style="font-size: 12px; margin-top: 4px;">Click any item on the left to preview</div>
          </div>

          <!-- 선택된 태스크 프리뷰 -->
          <div v-else class="flex-grow-1 overflow-y-auto pa-5">
            <div class="d-flex align-center ga-2 mb-1">
              <Icon icon="mdi:clipboard-text-outline" width="22" color="#1B84FF" />
              <span style="font-size: 16px; font-weight: 600;">{{ selectedTask.TASK_NAME }}</span>
            </div>
            <div v-if="capsOf(selectedTask).length" class="preview-caps">
              <span class="preview-caps-lbl">Available</span>
              <span
                v-for="c in capsOf(selectedTask)"
                :key="c.field"
                class="task-cap task-cap-lg"
                :style="{ color: c.color, background: c.bg }"
              >
                <Icon :icon="c.icon" width="14" />{{ c.label }}
              </span>
            </div>
            <div class="d-flex align-center justify-space-between mb-3" style="gap: 8px;">
              <div style="font-size: 12px; color: #888;">
                {{ parseDocCodes(selectedTask).length }} documents
                <span v-if="taskDocuments.length > 1" style="color:#b0b0b0;"> · drag to reorder</span>
              </div>
              <div style="font-size: 11px; min-height: 16px;">
                <span v-if="savingOrder" style="color:#5c6bc0;" class="d-inline-flex align-center ga-1">
                  <v-progress-circular indeterminate size="12" width="2" /> Saving order…
                </span>
                <span v-else-if="orderSaved" style="color:#2e7d32;" class="d-inline-flex align-center ga-1">
                  <Icon icon="mdi:check-circle-outline" width="14" /> Order saved
                </span>
              </div>
            </div>

            <!-- 문서 목록 -->
            <div v-if="loadingDocs" class="text-center py-4">
              <v-progress-circular indeterminate size="20" width="2" />
            </div>
            <div v-else-if="taskDocuments.length > 0">
              <div
                v-for="(doc, idx) in taskDocuments"
                :key="doc.docCode"
                class="doc-row"
                :class="{ 'doc-row-dragging': dragIndex === idx, 'doc-row-over': overIndex === idx && dragIndex !== idx }"
                draggable="true"
                @dragstart="onDocDragStart(idx, $event)"
                @dragover="onDocDragOver(idx, $event)"
                @drop="onDocDrop(idx, $event)"
                @dragend="onDocDragEnd"
              >
                <Icon icon="mdi:drag-vertical" width="18" class="doc-drag-handle" />
                <div class="doc-row-num">{{ idx + 1 }}</div>
                <Icon icon="mdi:file-document-outline" width="18" color="#5c6bc0" />
                <div class="doc-row-info">
                  <div
                    class="doc-row-name"
                    :style="{ color: 'rgb(var(--v-theme-on-surface))' }"
                  >
                    {{ doc.templateName }}
                  </div>
                  <div
                    class="doc-row-code"
                    :style="{ color: 'rgba(var(--v-theme-on-surface), 0.7)' }"
                  >
                    {{ doc.docCode }}
                  </div>
                </div>
              </div>
            </div>
            <div v-else style="font-size: 12px; color: #999;">
              No documents linked
            </div>
          </div>
        </div>
        </template>

        <!-- ===== T-Doc AI 안내 패널 ===== -->
        <div v-else-if="activeType === 'tdoc'" class="preset-content-pane preset-info-pane">
          <div class="info-callout">
            <b>Why T-Doc AI?</b> Presets use only the templates Lettaga provides. With T-Doc AI you can
            <b>upload your own templates</b> and generate documents from them.
          </div>
          <ul class="adv-list">
            <li><Icon icon="mdi:cloud-upload-outline" width="18" /> Freely upload your own templates — single, multiple or ZIP.</li>
            <li><Icon icon="mdi:folder-cog-outline" width="18" /> Manage uploaded templates &amp; assign variables in the left-menu <b>T-Doc</b>.</li>
            <li><Icon icon="mdi:robot-outline" width="18" /> The AI extracts &amp; fills the variables for you.</li>
            <li><Icon icon="mdi:draw-pen" width="18" /> Send generated documents to <b>T-Sign</b> for e-signature.</li>
          </ul>
          <div class="step-wiz">
            <template v-for="(s, i) in tdocSteps" :key="s">
              <span class="sw-step"><span class="sw-n">{{ i + 1 }}</span>{{ s }}</span>
              <span v-if="i < tdocSteps.length - 1" class="sw-line" />
            </template>
          </div>
          <div class="text-right mt-4">
            <v-btn color="primary" variant="flat" size="small" @click="openTDocAi">
              <Icon icon="mdi:creation" width="16" class="mr-1" />
              Open T-Doc AI
            </v-btn>
          </div>
        </div>

        <!-- ===== Prepare AR — entity picker (package StartTaskLauncher) ===== -->
        <div v-else class="preset-content-pane">
          <PrepareArEntityPanel
            :active="activeType === 'ar'"
            @prepare="onPrepareAr"
          />
        </div>
      </div>

      <!-- Footer — Preset / My Preset (T-Doc / Prepare AR proceed in-panel) -->
      <div
        v-if="isPresetView"
        class="d-flex align-center justify-space-between px-5 py-3"
        style="border-top: 1px solid #eee;"
      >
        <div style="font-size: 12px; color: #888;">
          {{ selectedTask ? selectedTask.TASK_NAME : 'No preSet selected' }}
        </div>
        <div class="d-flex ga-2">
          <v-btn
            v-if="showClassicMenuLink"
            variant="text"
            color="primary"
            size="small"
            @click="handleUseClassicMenu"
          >
            <Icon icon="mdi:view-grid-outline" width="16" class="mr-1" />
            Use Classic Card Menu
          </v-btn>
          <v-btn variant="outlined" color="grey" size="small" @click="handleCancel">
            Cancel
          </v-btn>
          <v-btn
            variant="flat"
            color="primary"
            size="small"
            :disabled="!selectedTask || loadingDocs"
            :loading="loadingDocs"
            @click="confirmSelect"
          >
            <Icon icon="mdi:check" width="16" class="mr-1" />
            Apply preSet
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>

  <!-- Start Task / E-Filing Tutorial -->
  <v-dialog
    v-model="showTutorial"
    :scrim="true"
    fullscreen
    transition="dialog-bottom-transition"
  >
    <v-card style="height: 100vh;">
      <v-toolbar density="comfortable" color="primary">
        <v-toolbar-title class="text-white">{{ activeTutorialTitle }} ({{ jurisdictionLabel }})</v-toolbar-title>
        <v-spacer />
        <v-btn
          icon
          variant="text"
          class="text-white"
          aria-label="Close tutorial"
          @click="showTutorial = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text style="padding: 0; height: calc(100vh - 56px); background: #0a1628;">
        <iframe
          v-if="showTutorial"
          :key="activeTutorialType"
          :src="activeTutorialSrc"
          :title="activeTutorialTitle"
          style="width: 100%; height: 100%; border: 0; display: block;"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.preset-modal-card {
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  height: min(680px, 88vh);
  min-height: 600px;
  overflow: hidden;
}

.preset-modal-body {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  overflow: hidden;
  margin-top: 8px;
}

.preset-content-pane {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ===== 타입 전환 컬럼 (mockup 시안 B) ===== */
.preset-type-col {
  width: 210px;
  flex-shrink: 0;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 10px;
  overflow-y: auto;
}
.preset-type-btn {
  display: flex;
  align-items: center;
  gap: 9px;
  text-align: left;
  border: 1px solid #e6eaf2;
  border-radius: 10px;
  padding: 9px 10px;
  background: #fff;
  color: #222b45;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.preset-type-btn:hover {
  border-color: #c7d2e6;
  background: #f8fafd;
}
.preset-type-btn.on {
  border-color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
}
.preset-type-btn .ptb-text {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  min-width: 0;
}
.preset-type-btn .ptb-text b {
  font-size: 13px;
}
.preset-type-btn .ptb-text small {
  font-size: 10px;
  color: #8a93a6;
}
.preset-type-btn.on .ptb-text small {
  color: rgb(var(--v-theme-primary), 0.8);
}

.preset-info-pane {
  overflow-y: auto;
  padding: 24px;
}
.preset-info-pane .info-callout {
  font-size: 12.5px;
  color: #46506a;
  background: #eef3ff;
  border: 1px solid #d8e4ff;
  border-radius: 10px;
  padding: 12px 14px;
  line-height: 1.55;
}
.preset-info-pane .adv-list {
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
}
.preset-info-pane .adv-list li {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 13px;
  color: #46506a;
  padding: 6px 0;
}
.preset-info-pane .adv-list li :deep(svg) {
  color: rgb(var(--v-theme-primary));
  flex-shrink: 0;
  margin-top: 1px;
}
.preset-info-pane .step-wiz {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;
  margin-top: 18px;
}
.preset-info-pane .sw-step {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #8a93a6;
  font-size: 11.5px;
  font-weight: 600;
}
.preset-info-pane .sw-n {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid #c9d2e3;
  display: grid;
  place-items: center;
  font-size: 10px;
}
.preset-info-pane .sw-line {
  width: 14px;
  height: 2px;
  background: #dde3ef;
  margin: 0 5px;
}

/* Dark theme fixes (Vuetify)
 * - v-dialog overlay 컨텍스트에서 scoped + :global 조합이 일부 케이스에서 매칭이 약해
 *   전체 셀렉터를 :global(...)로 감싸 강제 적용
 */
:global(.v-theme--dark .category-label),
:global(.v-theme--dark .selected-badge) {
  color: rgba(255, 255, 255, 0.78);
}
:global(.v-theme--dark .category-dot) {
  background: rgba(255, 255, 255, 0.55);
}
:global(.v-theme--dark .task-item:hover) {
  background: rgba(255, 255, 255, 0.06);
}
:global(.v-theme--dark .task-item-selected) {
  background: rgba(var(--v-theme-primary), 0.16);
}
:global(.v-theme--dark .task-icon) {
  background: rgba(255, 255, 255, 0.08);
}
:global(.v-theme--dark .task-item-selected .task-icon) {
  background: rgba(var(--v-theme-primary), 0.22);
}
:global(.v-theme--dark .task-name),
:global(.v-theme--dark .doc-row-name) {
  color: rgba(255, 255, 255, 0.98) !important;
  font-weight: 600;
}
:global(.v-theme--dark .task-meta),
:global(.v-theme--dark .doc-row-code) {
  color: rgba(255, 255, 255, 0.65);
}
:global(.v-theme--dark .doc-count-badge),
:global(.v-theme--dark .doc-row-num) {
  background: rgba(var(--v-theme-primary), 0.18);
  color: rgba(255, 255, 255, 0.9);
}
:global(.v-theme--dark .doc-row) {
  border-color: rgba(255, 255, 255, 0.12);
}
:global(.v-theme--dark .doc-row:hover) {
  background: rgba(255, 255, 255, 0.06);
}

.category-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 0 4px;
  font-size: 11px;
  font-weight: 700;
  color: #5c6bc0;
  letter-spacing: 0.3px;
}
.category-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #5c6bc0;
  flex-shrink: 0;
}
.selected-badge {
  color: #5c6bc0;
  font-weight: 600;
}

.task-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
}
.task-item:hover {
  background: rgba(var(--v-theme-primary), 0.08);
}
.task-item-selected {
  background: rgba(var(--v-theme-primary), 0.14);
}

.task-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.task-item-selected .task-icon {
  background: rgba(var(--v-theme-primary), 0.18);
}

.task-info {
  flex: 1;
  min-width: 0;
}
.task-name {
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.task-meta {
  font-size: 11px;
  color: #888;
  margin-top: 1px;
}
/* 업무 가능 단계 칩 (Document / T-Sign / E-Filing / Register) */
.task-caps {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.task-cap {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 999px;
  white-space: nowrap;
}
.task-cap-lg {
  font-size: 11.5px;
  padding: 4px 9px;
  gap: 4px;
}
.preview-caps {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 6px 0 10px;
}
.preview-caps-lbl {
  font-size: 11px;
  font-weight: 700;
  color: #888;
  margin-right: 2px;
}

.doc-count-badge {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #eef2ff;
  color: #5c6bc0;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.doc-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 8px;
  margin-bottom: 6px;
  transition: background 0.15s;
}
.doc-row:hover {
  background: rgba(var(--v-theme-primary), 0.06);
}
.doc-row:hover .doc-drag-handle {
  color: rgba(var(--v-theme-on-surface), 0.55);
}
.doc-drag-handle {
  color: rgba(var(--v-theme-on-surface), 0.25);
  cursor: grab;
  flex-shrink: 0;
}
.doc-drag-handle:active {
  cursor: grabbing;
}
.doc-row-dragging {
  opacity: 0.5;
}
.doc-row-over {
  border-color: rgba(var(--v-theme-primary), 0.9);
  box-shadow: inset 0 2px 0 rgba(var(--v-theme-primary), 0.9);
}
.doc-row-num {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: rgba(var(--v-theme-primary), 0.16);
  color: rgba(var(--v-theme-on-surface), 0.9);
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.doc-row-info {
  flex: 1;
  min-width: 0;
}
.doc-row-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}
.doc-row-code {
  font-size: 10px;
  color: #999;
  margin-top: 1px;
}
</style>
